import {
  ArrowLeft,
  ArrowRight,
  Building,
  Check,
  ChevronDown,
  FileText,
  Loader,
  MessageSquare,
  Plus,
  Search,
  User,
  Users,
  X,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import manpowerService from '../../services/manpowerService';
import { ManpowerFormData, ManpowerLocation, Project, ProjectLocation } from '../../types';
import { LocationInput } from '../forms';
import AddCompanyModal from '../modals/AddCompanyModal';
import AddWorkerModal from '../modals/AddWorkerModal';

// Adapter functions to convert between location types
const manpowerToProjectLocation = (manpowerLocation: ManpowerLocation): ProjectLocation => {
  const address = [manpowerLocation.site, manpowerLocation.building, manpowerLocation.floor]
    .filter(Boolean)
    .join(', ');

  const coords = manpowerLocation.gps
    ? manpowerLocation.gps.split(',').map(c => parseFloat(c.trim()))
    : undefined;

  return {
    address: address || manpowerLocation.site || '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    coordinates:
      coords && coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])
        ? { lat: coords[0], lng: coords[1] }
        : undefined,
  };
};

const projectToManpowerLocation = (projectLocation: ProjectLocation): ManpowerLocation => {
  return {
    site: projectLocation.address || '',
    building: '',
    floor: '',
    gps: projectLocation.coordinates
      ? `${projectLocation.coordinates.lat}, ${projectLocation.coordinates.lng}`
      : '',
  };
};

// Simple dropdown option type for manpower components
interface DropdownOption {
  value: string;
  label: string;
  metadata?: any;
}

// Enhanced types for new flow
interface WorkerOption extends DropdownOption {
  type: 'worker';
  metadata: {
    role: string;
    hourlyRate: number;
    overtimeRate: number;
    phone?: string;
    email?: string;
  };
}

interface CompanyOption extends DropdownOption {
  type: 'company';
  metadata: {
    defaultRate: number;
    workerCount: number;
    contactPerson?: string;
    phone?: string;
  };
}

type SelectableOption = WorkerOption | CompanyOption;

interface ManpowerTableRow {
  id: string;
  option: SelectableOption;
  role: string;
  workerCount: number; // For companies
  hours: number;
  rate: number;
  overtime: number;
  overtimeRate: number;
  comments: string;
}

interface ManpowerEntryFormProps {
  onCancel: () => void;
  initialData?: Partial<ManpowerFormData>;
  projects: Project[];
  isSubmitting?: boolean;
  projectId?: string;
}

const ManpowerEntryForm: React.FC<ManpowerEntryFormProps> = ({
  onCancel,
  initialData,
  projects,
  isSubmitting = false,
  projectId,
}) => {
  const toast = useToast();
  const { t } = useTranslation();

  // Generate unique ID for new entries
  const generateEntryId = () =>
    `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${performance.now()}`;

  // Two-step flow state
  const [currentStep, setCurrentStep] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<SelectableOption[]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showLocationEdit, setShowLocationEdit] = useState(false);

  // Tooltip editing state
  const [editingTooltip, setEditingTooltip] = useState<{
    rowId: string;
    field: string;
    value: any;
    position: { top: number; left: number };
  } | null>(null);
  const [tempValue, setTempValue] = useState<any>('');
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Bulk rate update state
  const [showBulkRateModal, setShowBulkRateModal] = useState(false);
  const [bulkRateValue, setBulkRateValue] = useState('');
  const bulkRateRef = useRef<HTMLDivElement>(null);

  // Table rows for step 2
  const [tableRows, setTableRows] = useState<ManpowerTableRow[]>([]);

  // Image upload state (managed by form data)

  // Form state
  const [formData, setFormData] = useState<ManpowerFormData>({
    project_id: initialData?.project_id || projectId || '',
    location: initialData?.location || {
      site: '',
      building: '',
      floor: '',
      gps: '',
    },
    date: initialData?.date || new Date().toISOString().split('T')[0],
    entries: [],
    attachments: initialData?.attachments || [],
    attachmentIds: initialData?.attachmentIds || [],
  });

  // Dynamic dropdown state
  const [allOptions, setAllOptions] = useState<SelectableOption[]>([]);
  const [workerTypes, setWorkerTypes] = useState<DropdownOption[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);

  // Get current project info
  const currentProject = projects.find(p => p.id === projectId) || projects[0];

  // Modal states
  const [showAddWorkerModal, setShowAddWorkerModal] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [submittingWorker, setSubmittingWorker] = useState(false);
  const [submittingCompany, setSubmittingCompany] = useState(false);

  // Form state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load dynamic dropdown data
  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    setLoadingDropdowns(true);
    try {
      const [workersData, companiesData, workerTypesData] = await Promise.all([
        manpowerService.getWorkersForDropdown(),
        manpowerService.getCompaniesForDropdown(),
        manpowerService.getWorkerTypes(),
      ]);

      // Combine workers and companies into unified options
      const workers: WorkerOption[] = workersData.map(w => ({
        ...w,
        type: 'worker' as const,
        metadata: {
          role: w.metadata?.specialization || 'General',
          hourlyRate: w.metadata?.hourlyRate || 25,
          overtimeRate: w.metadata?.hourlyRate * 1.5 || 37.5,
          phone: w.metadata?.phone,
          email: w.metadata?.email,
        },
      }));

      const companies: CompanyOption[] = companiesData.map(c => ({
        ...c,
        type: 'company' as const,
        metadata: {
          defaultRate: 22, // Default rate for companies
          workerCount: 5, // Default worker count
          contactPerson: c.metadata?.contactPerson,
          phone: c.metadata?.phone,
        },
      }));

      // Combine and deduplicate options
      const combinedOptions = [...workers, ...companies];

      // Remove duplicates based on value and type combination
      const uniqueOptions = combinedOptions.filter(
        (option, index, array) =>
          array.findIndex(item => item.value === option.value && item.type === option.type) ===
          index
      );

      setAllOptions(uniqueOptions);
      setWorkerTypes(workerTypesData);
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  // Filter options based on search
  const filteredOptions = allOptions.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle option selection in step 1
  const handleOptionToggle = (option: SelectableOption) => {
    setSelectedOptions(prev => {
      const isSelected = prev.some(o => o.value === option.value && o.type === option.type);
      if (isSelected) {
        return prev.filter(o => !(o.value === option.value && o.type === option.type));
      } else {
        return [...prev, option];
      }
    });
  };

  // Convert selected options to table rows for step 2
  const proceedToStep2 = () => {
    const rows: ManpowerTableRow[] = selectedOptions.map(option => ({
      id: generateEntryId(),
      option,
      role: option.type === 'worker' ? option.metadata.role : 'General',
      workerCount: option.type === 'company' ? option.metadata.workerCount : 1,
      hours: 8,
      rate: option.type === 'worker' ? option.metadata.hourlyRate : option.metadata.defaultRate,
      overtime: 0,
      overtimeRate:
        option.type === 'worker' ? option.metadata.overtimeRate : option.metadata.defaultRate * 1.5,
      comments: '',
    }));
    setTableRows(rows);
    setCurrentStep(2);
  };

  // Row selection handlers
  const handleRowToggle = (rowId: string) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.size === tableRows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(tableRows.map(row => row.id)));
    }
  };

  // Bulk operations
  const handleBulkDelete = () => {
    if (confirm(`Delete ${selectedRows.size} selected entries?`)) {
      setTableRows(prev => prev.filter(row => !selectedRows.has(row.id)));
      setSelectedRows(new Set());
    }
  };

  const handleBulkRateUpdate = () => {
    setShowBulkRateModal(true);
    setBulkRateValue('');
  };

  const handleApplyBulkRate = () => {
    const newRate = Number(bulkRateValue);
    if (!isNaN(newRate) && newRate > 0) {
      setTableRows(prev =>
        prev.map(row =>
          selectedRows.has(row.id) ? { ...row, rate: newRate, overtimeRate: newRate * 1.5 } : row
        )
      );
      setSelectedRows(new Set());
      setShowBulkRateModal(false);
      setBulkRateValue('');
    }
  };

  const handleCancelBulkRate = () => {
    setShowBulkRateModal(false);
    setBulkRateValue('');
  };

  // Tooltip editing handlers
  const handleStartEdit = (
    rowId: string,
    field: string,
    currentValue: any,
    event: React.MouseEvent
  ) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setEditingTooltip({
      rowId,
      field,
      value: currentValue,
      position: {
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      },
    });
    setTempValue(currentValue);
  };

  const handleApplyEdit = () => {
    if (editingTooltip) {
      setTableRows(prev =>
        prev.map(row =>
          row.id === editingTooltip.rowId ? { ...row, [editingTooltip.field]: tempValue } : row
        )
      );
    }
    setEditingTooltip(null);
    setTempValue('');
  };

  const handleCancelEdit = () => {
    setEditingTooltip(null);
    setTempValue('');
  };

  // Close tooltip on outside click and keyboard shortcuts
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        handleCancelEdit();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancelEdit();
      } else if (event.key === 'Enter' && !event.shiftKey) {
        if (editingTooltip?.field !== 'comments') {
          event.preventDefault();
          handleApplyEdit();
        }
      }
    };

    if (editingTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [editingTooltip]);

  // Handle bulk rate modal keyboard and outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkRateRef.current && !bulkRateRef.current.contains(event.target as Node)) {
        handleCancelBulkRate();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancelBulkRate();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        handleApplyBulkRate();
      }
    };

    if (showBulkRateModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showBulkRateModal, bulkRateValue]);

  // Updated comment editing to use tooltip
  const handleStartCommentEdit = (rowId: string, event: React.MouseEvent) => {
    const row = tableRows.find(r => r.id === rowId);
    if (row) {
      handleStartEdit(rowId, 'comments', row.comments, event);
    }
  };

  // Handle adding new worker/company
  const handleAddWorker = async (workerData: any) => {
    setSubmittingWorker(true);
    try {
      const response = await manpowerService.createWorker(workerData);
      if (response.success) {
        await loadDropdownData();
        setShowAddWorkerModal(false);
        toast.success(t('manpower.toast.workerAddedSuccess'));
      } else {
        toast.error(t('manpower.toast.workerAddFailed'), response.message);
      }
    } catch (error) {
      console.error('Error adding worker:', error);
      toast.error(t('manpower.toast.workerAddFailedRetry'));
    } finally {
      setSubmittingWorker(false);
    }
  };

  const handleAddCompany = async (companyData: any) => {
    setSubmittingCompany(true);
    try {
      const response = await manpowerService.createCompany(companyData);
      if (response.success) {
        await loadDropdownData();
        setShowAddCompanyModal(false);
        toast.success(t('manpower.toast.companyAddedSuccess'));
      } else {
        toast.error(t('manpower.toast.companyAddFailed'), response.message);
      }
    } catch (error) {
      console.error('Error adding company:', error);
      toast.error(t('manpower.toast.companyAddFailedRetry'));
    } finally {
      setSubmittingCompany(false);
    }
  };

  // Calculate totals from table rows
  const calculateTotals = () => {
    return tableRows.reduce(
      (acc, row) => {
        const workerMultiplier = row.option.type === 'company' ? row.workerCount : 1;
        const regularPay = row.hours * row.rate * workerMultiplier;
        const overtimePay = row.overtime * row.overtimeRate * workerMultiplier;
        const entryTotal = regularPay + overtimePay;

        return {
          regularPay: acc.regularPay + regularPay,
          overtimePay: acc.overtimePay + overtimePay,
          total: acc.total + entryTotal,
          totalHours: acc.totalHours + (row.hours + row.overtime) * workerMultiplier,
          entryCount: acc.entryCount + workerMultiplier,
        };
      },
      { regularPay: 0, overtimePay: 0, total: 0, totalHours: 0, entryCount: 0 }
    );
  };

  // Validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) newErrors.date = t('manpower.validation.workDateRequired');
    if (!formData.location.site) newErrors.site = t('manpower.validation.workLocationRequired');
    if (tableRows.length === 0) newErrors.entries = t('manpower.validation.atLeastOneEntry');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Progress tracking for submission
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [submissionStatus, setSubmissionStatus] = useState('');

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      // Show validation errors to user
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        toast.warning(t('manpower.validation.fixErrors'), errorMessages.join('\n'));
      }
      return;
    }

    try {
      setSubmissionProgress(0);
      setSubmissionStatus(t('manpower.preparingSubmissions'));

      // Create a single submission with all entries
      const submissionData: ManpowerFormData = {
        project_id: formData.project_id,
        location: formData.location,
        date: formData.date,
        entries: tableRows.map(row => ({
          id: row.id,
          entry_type: row.option.type === 'worker' ? 'individual' : 'company',
          worker_id: row.option.type === 'worker' ? row.option.value : '',
          worker_name: row.option.type === 'worker' ? row.option.label : '',
          company_id: row.option.type === 'company' ? row.option.value : '',
          company_name: row.option.type === 'company' ? row.option.label : '',
          number_of_workers: row.option.type === 'company' ? row.workerCount : 1,
          role: row.role,
          hours_worked: row.hours,
          overtime_hours: row.overtime,
          hourly_rate: row.rate,
          overtime_rate: row.overtimeRate,
          comments: row.comments,
        })),
      };

      // Submit the bulk entry data
      setSubmissionStatus(t('manpower.submittingEntries'));
      setSubmissionProgress(50);

      const result = await manpowerService.createEntry(submissionData, progress => {
        setSubmissionProgress(progress);
      });

      if (result.success) {
        setSubmissionProgress(100);
        setSubmissionStatus(`Successfully submitted all ${tableRows.length} entries!`);

        setTimeout(() => {
          onCancel(); // Navigate back after success
        }, 2000);
      } else {
        throw new Error(result.message || t('manpower.submissionFailed'));
      }
    } catch (error) {
      console.error('Error during submission process:', error);
      setSubmissionStatus(t('manpower.submissionFailed'));
      toast.error(t('manpower.toast.submitEntriesFailed'));
    }
  };

  if (loadingDropdowns) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="h-6 w-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-600">Loading form data...</span>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="h-full flex flex-col">
      <form onSubmit={handleSubmit} className="h-full flex flex-col">
        {/* Step Indicator */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  currentStep >= 1 ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {currentStep > 1 ? <Check className="h-4 w-4" /> : '1'}
              </div>
              <div className={`h-1 w-16 ${currentStep >= 2 ? 'bg-red-500' : 'bg-gray-200'}`} />
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  currentStep >= 2 ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}
              >
                2
              </div>
            </div>
            <div className="text-sm text-gray-600">Step {currentStep} of 2</div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {currentStep === 1 ? (
            /* STEP 1: Worker/Company Selection */
            <div className="p-6 space-y-6">
              {/* Header */}
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Select Workers & Companies
                </h2>
                <p className="text-gray-600">
                  {t('manpower.chooseWorkers', {
                    project: currentProject?.name || t('manpower.thisProject'),
                    date: formData.date,
                  })}
                </p>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('manpower.searchPlaceholder')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                >
                  Yesterday's Crew
                </button>
                <button
                  type="button"
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                >
                  Electrical Team
                </button>
                <button
                  type="button"
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                >
                  Common Workers
                </button>
              </div>

              {/* Options Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredOptions.map(option => {
                  const isSelected = selectedOptions.some(
                    o => o.value === option.value && o.type === option.type
                  );
                  return (
                    <div
                      key={`${option.type}-${option.value}`}
                      onClick={() => handleOptionToggle(option)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-red-500 bg-red-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                            option.type === 'worker' ? 'bg-brand-100' : 'bg-purple-100'
                          }`}
                        >
                          {option.type === 'worker' ? (
                            <User className="h-5 w-5 text-brand-primary" />
                          ) : (
                            <Users className="h-5 w-5 text-purple-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{option.label}</h3>
                          <p className="text-sm text-gray-600">
                            {option.type === 'worker'
                              ? `${option.metadata.role} • $${option.metadata.hourlyRate}/h`
                              : `${option.metadata.workerCount} workers • $${option.metadata.defaultRate}/h`}
                          </p>
                        </div>
                        {isSelected && <Check className="h-5 w-5 text-red-500" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Selected Count */}
              {selectedOptions.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-red-800 font-medium">
                      {selectedOptions.length} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedOptions([])}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* STEP 2: Hours & Details Table */
            <div className="p-6 space-y-6">
              {/* Header with Project Info */}
              <div
                className={`border rounded-lg p-4 ${!formData.location.site ? 'bg-red-50 border-red-200' : 'bg-brand-50 border-brand-200'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Building
                      className={`h-5 w-5 ${!formData.location.site ? 'text-red-600' : 'text-brand-primary'}`}
                    />
                    <div>
                      <h3
                        className={`font-semibold ${!formData.location.site ? 'text-red-900' : 'text-brand-900'}`}
                      >
                        {currentProject?.name || t('manpower.projectLabel')}
                      </h3>
                      <div
                        className={`flex items-center space-x-4 text-sm ${!formData.location.site ? 'text-red-700' : 'text-brand-700'}`}
                      >
                        <span>{formData.date}</span>
                        <span>•</span>
                        <span
                          className={!formData.location.site ? 'font-semibold text-red-700' : ''}
                        >
                          {formData.location.site || '⚠️ Location required - Click Edit to set'}
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowLocationEdit(!showLocationEdit)}
                          className={`${!formData.location.site ? 'text-red-600 hover:text-red-800 font-semibold' : 'text-brand-primary hover:text-brand-800'}`}
                        >
                          {!formData.location.site
                            ? t('manpower.setLocation')
                            : t('manpower.editLocation')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Editor */}
                {(showLocationEdit || !formData.location.site) && (
                  <div
                    className={`mt-4 pt-4 border-t ${!formData.location.site ? 'border-red-200' : 'border-brand-200'}`}
                  >
                    <LocationInput
                      label={t('manpower.workLocation')}
                      value={manpowerToProjectLocation(formData.location)}
                      onChange={(projectLocation: ProjectLocation) => {
                        const manpowerLocation = projectToManpowerLocation(projectLocation);
                        setFormData(prev => ({ ...prev, location: manpowerLocation }));
                        // Clear location error when location is set
                        if (manpowerLocation.site && errors.site) {
                          setErrors(prev => ({ ...prev, site: '' }));
                        }
                      }}
                      error={errors.site}
                      required
                      placeholder={t('manpower.searchLocationPlaceholder')}
                    />
                    {!formData.location.site && (
                      <p className="mt-2 text-sm text-red-600 font-medium">
                        ⚠️ Work location is required to submit manpower entries
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Bulk Actions Bar */}
              {selectedRows.size > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-yellow-800 font-medium">
                      {selectedRows.size} entries selected
                    </span>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handleBulkRateUpdate}
                        className="px-3 py-1 text-sm bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200"
                      >
                        Update Rate
                      </button>
                      <button
                        type="button"
                        onClick={handleBulkDelete}
                        className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedRows.size === tableRows.length && tableRows.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-gray-300 text-red-500 focus:ring-red-500"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Worker/Company
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Role
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Count
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Hours
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Rate
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          OT
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          💬
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          📎
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {tableRows.map(row => (
                        <React.Fragment key={row.id}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedRows.has(row.id)}
                                onChange={() => handleRowToggle(row.id)}
                                className="rounded border-gray-300 text-red-500 focus:ring-red-500"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-3">
                                <div
                                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    row.option.type === 'worker' ? 'bg-brand-100' : 'bg-purple-100'
                                  }`}
                                >
                                  {row.option.type === 'worker' ? (
                                    <User className="h-4 w-4 text-brand-primary" />
                                  ) : (
                                    <Users className="h-4 w-4 text-purple-600" />
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {row.option.label}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {row.option.type === 'worker'
                                      ? t('manpower.individual')
                                      : t('manpower.company')}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={e => handleStartEdit(row.id, 'role', row.role, e)}
                                className="text-left hover:bg-gray-100 px-2 py-1 rounded w-full flex items-center justify-between group"
                              >
                                <span>{row.role}</span>
                                <ChevronDown className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              {row.option.type === 'company' ? (
                                <button
                                  onClick={e =>
                                    handleStartEdit(row.id, 'workerCount', row.workerCount, e)
                                  }
                                  className="hover:bg-gray-100 px-2 py-1 rounded text-brand-primary font-medium"
                                >
                                  {row.workerCount}
                                </button>
                              ) : (
                                <span className="text-gray-400">1</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={e => handleStartEdit(row.id, 'hours', row.hours, e)}
                                className="hover:bg-gray-100 px-2 py-1 rounded"
                              >
                                {row.hours}h
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={e => handleStartEdit(row.id, 'rate', row.rate, e)}
                                className="hover:bg-gray-100 px-2 py-1 rounded"
                              >
                                ${row.rate}
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={e => handleStartEdit(row.id, 'overtime', row.overtime, e)}
                                className="hover:bg-gray-100 px-2 py-1 rounded"
                              >
                                {row.overtime}h
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={e => handleStartCommentEdit(row.id, e)}
                                className={`p-2 rounded-full transition-colors ${
                                  row.comments
                                    ? 'text-red-500 bg-red-50 hover:bg-red-100'
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                }`}
                                title={
                                  row.comments
                                    ? t('manpower.editComment')
                                    : t('manpower.addComment')
                                }
                              >
                                <MessageSquare className="h-4 w-4" />
                              </button>
                            </td>

                            {/* Attachments Column */}
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  // TODO: Implement attachment functionality
                                  console.log('Attachment clicked for row:', row.id);
                                }}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded"
                                title="Add attachment"
                              >
                                📎
                              </button>
                            </td>

                            <td className="px-4 py-3 font-semibold">
                              $
                              {(
                                (row.hours * row.rate + row.overtime * row.overtimeRate) *
                                (row.option.type === 'company' ? row.workerCount : 1)
                              ).toFixed(2)}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Add More Button */}
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-red-400 hover:text-red-600 transition-colors"
              >
                <Plus className="h-5 w-5 mx-auto mb-1" />
                Add More Workers
              </button>
            </div>
          )}
        </div>

        {/* Sticky Footer with Summary */}
        <div className="flex-shrink-0 bg-white border-t border-gray-200">
          {/* Green Summary Bar */}
          {currentStep === 2 && (
            <div className="bg-green-50 border-b border-green-200 px-6 py-4">
              {/* Submission Progress Bar */}
              {submissionProgress > 0 && submissionProgress < 100 && (
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-green-700">{submissionStatus}</span>
                    <span className="text-sm text-green-600">
                      {Math.round(submissionProgress)}%
                    </span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${submissionProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Totals Grid */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <div className="text-xs font-medium text-green-600 uppercase tracking-wider">
                    {t('manpower.totalEntries')}
                  </div>
                  <div className="text-lg font-bold text-green-800">{totals.entryCount}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-green-600 uppercase tracking-wider">
                    {t('manpower.totalHours')}
                  </div>
                  <div className="text-lg font-bold text-green-800">
                    {totals.totalHours.toFixed(1)}h
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-green-600 uppercase tracking-wider">
                    {t('manpower.regularPay')}
                  </div>
                  <div className="text-lg font-bold text-green-800">
                    ${totals.regularPay.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-green-600 uppercase tracking-wider">
                    {t('manpower.totalPayable')}
                  </div>
                  <div className="text-xl font-bold text-green-900">${totals.total.toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex space-x-3">
              {currentStep === 2 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('manpower.previous')}
                </button>
              )}
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>

            <div className="flex space-x-3">
              {currentStep === 1 ? (
                <button
                  type="button"
                  onClick={proceedToStep2}
                  disabled={selectedOptions.length === 0}
                  className="inline-flex items-center px-6 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('manpower.continue')}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    tableRows.length === 0 ||
                    submissionProgress > 0 ||
                    !formData.location.site ||
                    !formData.date
                  }
                  className="inline-flex items-center px-6 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={
                    !formData.location.site
                      ? t('manpower.validation.setLocationFirst')
                      : !formData.date
                        ? t('manpower.validation.setDateFirst')
                        : ''
                  }
                >
                  {isSubmitting || submissionProgress > 0 ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      {submissionStatus || t('manpower.submitting')}
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      {t('manpower.submitEntries', {
                        count: totals.entryCount,
                        entries:
                          totals.entryCount === 1 ? t('manpower.entry') : t('manpower.entries'),
                      })}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Editing Tooltip */}
      {editingTooltip &&
        createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-[1000] bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-64"
            style={{
              top: editingTooltip.position.top,
              left: editingTooltip.position.left,
            }}
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900 capitalize">
                  Edit {editingTooltip.field}
                </h4>
                <button onClick={handleCancelEdit} className="text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div>
                {editingTooltip.field === 'role' ? (
                  <select
                    value={tempValue}
                    onChange={e => setTempValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    autoFocus
                  >
                    {workerTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                ) : editingTooltip.field === 'comments' ? (
                  <textarea
                    value={tempValue || ''}
                    onChange={e => setTempValue(e.target.value)}
                    placeholder={t('manpower.addYourComment')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    rows={3}
                    autoFocus
                  />
                ) : (
                  <input
                    type="number"
                    value={tempValue}
                    onChange={e => setTempValue(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder={
                      editingTooltip.field === 'rate'
                        ? t('manpower.enterHourlyRate')
                        : editingTooltip.field === 'workerCount'
                          ? t('manpower.numberOfWorkers')
                          : t('manpower.enterHours')
                    }
                    min={editingTooltip.field === 'workerCount' ? 1 : 0}
                    step={editingTooltip.field === 'rate' ? 0.01 : 0.5}
                    autoFocus
                  />
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyEdit}
                  className="px-3 py-1.5 text-sm text-white bg-red-500 border border-red-500 rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-1"
                >
                  <Check className="h-3 w-3" />
                  <span>Apply</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Bulk Rate Update Modal */}
      {showBulkRateModal &&
        createPortal(
          <div className="fixed inset-0 z-[1000] flex items-center justify-center">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/30 backdrop-blur-sm"
              onClick={handleCancelBulkRate}
            />

            {/* Modal */}
            <div
              ref={bulkRateRef}
              className="relative bg-white rounded-xl shadow-2xl border border-gray-200 p-6 min-w-96 mx-4"
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('manpower.updateHourlyRate')}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('manpower.applyNewRate', {
                        count: selectedRows.size,
                        entries:
                          selectedRows.size === 1 ? t('manpower.entry') : t('manpower.entries'),
                      })}
                    </p>
                  </div>
                  <button
                    onClick={handleCancelBulkRate}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('manpower.newHourlyRate')}
                  </label>
                  <input
                    type="number"
                    value={bulkRateValue}
                    onChange={e => setBulkRateValue(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                    placeholder={t('manpower.enterNewRate')}
                    min="0"
                    step="0.01"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('manpower.overtimeRateNote')}</p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={handleCancelBulkRate}
                    className="px-4 py-2 text-sm text-gray-600 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApplyBulkRate}
                    disabled={
                      !bulkRateValue || isNaN(Number(bulkRateValue)) || Number(bulkRateValue) <= 0
                    }
                    className="px-4 py-2 text-sm text-white bg-red-500 border border-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                  >
                    <Check className="h-4 w-4" />
                    <span>
                      {t('manpower.updateEntries', {
                        count: selectedRows.size,
                        entries:
                          selectedRows.size === 1 ? t('manpower.entry') : t('manpower.entries'),
                      })}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Add Worker Modal */}
      <AddWorkerModal
        isOpen={showAddWorkerModal}
        onClose={() => setShowAddWorkerModal(false)}
        onSubmit={handleAddWorker}
        isSubmitting={submittingWorker}
      />

      {/* Add Company Modal */}
      <AddCompanyModal
        isOpen={showAddCompanyModal}
        onClose={() => setShowAddCompanyModal(false)}
        onSubmit={handleAddCompany}
        isSubmitting={submittingCompany}
      />
    </div>
  );
};

export default ManpowerEntryForm;
